import { useCallback, useRef } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import {
  tapelineDtoToTapeline,
  tapelineToCreateDTO,
  completeTapelineInput,
  tapelineToModifyDTO,
} from '@editor/stores/tapelineV2Store/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { Tapeline, TapelineInput, useTapelineStore } from '@editorStores'
import { getFilterViews } from '@queries'
import {
  createTapelines,
  deleteTapelines,
  TapelineCreateDTO,
  TapelineDTO,
  updateTapeline,
} from '@mutations'
import { usePersistentParams } from 'src/hooks'

const useTapelineMutations = () => {
  const setLines = useTapelineStore(state => state.setLines)
  const activeFilterViewId = useTapelineStore(state => state.activeFilterViewId)
  const updateLineAttributes = useTapelineStore(state => state.updateLineAttributes)
  const { projectId }: { projectId?: string } = usePersistentParams()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  const chainRef = useRef<Promise<unknown>>(Promise.resolve())
  const pendingIdsRef = useRef<Set<string>>(new Set())

  const applyTapelineResult = useCallback(
    (dtos: TapelineDTO[]) => {
      const currentViewLines = dtos.map(tapelineDtoToTapeline)
      setLines(currentViewLines)

      const selectedId = useTapelineStore.getState().selectedId
      if (selectedId && !currentViewLines.some(line => line.id === selectedId)) {
        useTapelineStore.getState().deselectLine()
      }
    },
    [setLines],
  )

  const createMutation = useMutation(
    (tapelines: TapelineCreateDTO[]) => createTapelines.request(projectId, tapelines),
    {
      mutationKey: ['createTapelines', projectId],
      meta: {
        title: 'Maßband erstellen',
        description: 'Erstellt Maßbänder.',
      },
      onSuccess: tapelines => applyTapelineResult(tapelines),
      onError: error =>
        enqueueSnackbar(
          buildErrorMessage(error as AxiosError, 'Fehler beim Erstellen des Maßbands'),
          { variant: 'error' },
        ),
    },
  )

  const updateMutation = useMutation(
    (id: string) => {
      const tapeline = useTapelineStore.getState().lines.find(line => line.id === id)
      if (!tapeline) return Promise.resolve(null)
      return updateTapeline.request(projectId, id, tapelineToModifyDTO(tapeline))
    },
    {
      mutationKey: ['updateTapeline', projectId],
      meta: {
        title: 'Maßband aktualisieren',
        description: 'Speichert Änderungen an einem Maßband.',
      },
      onError: async error => {
        enqueueSnackbar(
          buildErrorMessage(error as AxiosError, 'Fehler beim Aktualisieren des Maßbands'),
          { variant: 'error' },
        )
        await queryClient.invalidateQueries({ queryKey: getFilterViews.getKey(projectId) })
      },
    },
  )

  const deleteMutation = useMutation((ids: string[]) => deleteTapelines.request(projectId, ids), {
    mutationKey: ['deleteTapelines', projectId],
    meta: {
      title: 'Maßband löschen',
      description: 'Löscht Maßbänder.',
    },
    onSuccess: tapelines => applyTapelineResult(tapelines),
    onError: error =>
      enqueueSnackbar(buildErrorMessage(error as AxiosError, 'Fehler beim Löschen des Maßbands'), {
        variant: 'error',
      }),
  })

  const { mutateAsync: runCreate } = createMutation
  const { mutateAsync: runUpdate } = updateMutation
  const { mutate: runDelete, mutateAsync: runDeleteAsync } = deleteMutation

  const createTapelineFromInput = useCallback(
    async (line: TapelineInput) => {
      if (!activeFilterViewId) return
      const dto = tapelineToCreateDTO(completeTapelineInput(line, activeFilterViewId))
      await runCreate([dto])
    },
    [activeFilterViewId, runCreate],
  )

  const updateTapelineFields = useCallback(
    (id: string, changes: Partial<Tapeline>) => {
      updateLineAttributes(id, changes)

      if (pendingIdsRef.current.has(id)) return
      pendingIdsRef.current.add(id)

      const task = async () => {
        pendingIdsRef.current.delete(id)
        if (!useTapelineStore.getState().lines.some(line => line.id === id)) return
        await runUpdate(id).catch(() => undefined)
      }

      chainRef.current = chainRef.current.catch(() => undefined).then(task)
    },
    [updateLineAttributes, runUpdate],
  )

  const deleteTapelineById = useCallback(
    (id: string) => {
      runDelete([id])
    },
    [runDelete],
  )

  const deleteTapelinesInActiveFilterView = useCallback(() => {
    if (!activeFilterViewId) return
    const ids = useTapelineStore
      .getState()
      .lines.filter(line => line.filterViewId === activeFilterViewId)
      .map(line => line.id)
    if (ids.length === 0) return
    runDelete(ids)
  }, [activeFilterViewId, runDelete])

  return {
    createTapelineFromInput,
    updateTapelineFields,
    deleteTapeline: deleteTapelineById,
    deleteTapelinesInActiveFilterView,
    runDeleteAsync,
  }
}

export default useTapelineMutations
